import { useEffect, useMemo } from 'react';
import type { FC } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { Redirect, useLocation, useParams } from 'react-router';
import { useResizeObserver } from 'usehooks-ts';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import type { Location } from 'history';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { useSSRPreviewMode } from '../../bff-api/customers/useSSRPreviewMode';
import { BFFHttpError } from '../../bff-api/model/BFFHttpError';
import { EnhancedError } from '../../components/_error/EnhancedError';
import { StickyAddToBagSection } from '../../components/add-to-bag-section/AddToBagSection';
import { ClientOnly } from '../../components/client-only/ClientOnly';
import { LoadingIndicator } from '../../components/loading-indicator/LoadingIndicator';
import { PhotoGrid } from '../../components/photo-grid/PhotoGrid';
import { ProductInformation } from '../../components/product-information';
import ClientRecommendations from '../../components/recommendations/ClientRecommendations';
import { RecommendationLocation } from '../../components/recommendations/RecommendationLocation';
import { getSourcePropsByWidth } from '../../components/responsive-image/response-image-utils';
import Seo from '../../components/seo';
import { TurnTo } from '../../components/turn-to';
import { TurnToSkeleton } from '../../components/turn-to/TurnToSkeleton';
import { WriteReviewFlow } from '../../components/turn-to/components';
import { WidgetRecommendationName } from '../../constants/WidgetRecommendationName';
import { AuthType } from '../../contexts/model/AuthType';
import { PdpProvider, usePDP } from '../../contexts/pdp/usePDP';
import { SSRProductProvider } from '../../contexts/pdp/useSSRProduct';
import { ProductsAddedToBagDrawer } from '../../contexts/products-added-to-bag-drawer';
import { TurnToProvider } from '../../contexts/useTurnTo';
import {
    TurnToComponentDataProvider,
    useTurnToComponentData,
} from '../../contexts/useTurnToComponentData';
import { useWidgetRecommendations } from '../../hooks/useWidgetRecommendations';
import { Breadcrumb } from '../../ui-components';
import { useBreadcrumbs } from '../../ui-components/breadcrumbs/useBreadcrumbs';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { buildHttpError } from '../../utils/error-utils';
import { isNoIndexPage } from '../../utils/is-no-index-page';
import {
    BreadcrumbSchema,
    ImageSchema,
    OpenGraph,
    OrganizationSchema,
    ProductSchema,
    QuestionSchema,
    VideoSchema,
} from '../../utils/schema';
import { isServer } from '../../utils/utils';
import { CanonicalCategory, getCanonicalUrl } from '../utils/canonical-url';
import { PageUrl } from '../utils/page-url';
import { PDPHead } from './PDPHead';
import { PDPSkeleton } from './PDPSkeleton';
import { BuyBox } from './buy-box';
import { useGetTopBuyBoxPosition } from './hooks/useGetTopBuyBoxPosition';
import { ProductRelatedCategories } from './product-related-categories/ProductRelatedCategories';
import type { Product } from '../../bff-api/products/model';
import type { AssetInfo } from '../../components/photo-grid/utils';
import type { PDPLocationState } from './types';

const PDP_MAX_WIDTH = 1792;

const PDPLayout: FC<{
    sku: string;
    pageURL: string;
    headerRef?: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ sku, pageURL, headerRef }) => {
    const isSSRPreviewMode = useSSRPreviewMode();
    const {
        isLoading,
        product,
        isError,
        error,
        getElementRef,
        registerElementRef,
        registerElementExistingRef,
    } = usePDP();

    const { data: customer, isLoading: isCustomerGetLoading } = useCustomerGet();
    const userId =
        customer?.authType === AuthType.REGISTERED ? customer?.customerNo : customer?.customerId;

    useEffect(() => {
        if (headerRef) {
            registerElementExistingRef('layout-header', headerRef);
        }
    }, [registerElementExistingRef, headerRef]);

    const breadcrumbs = useBreadcrumbs(product?.c_primaryCategory?.parentCategoryTree);

    const recommendationWidgets = useWidgetRecommendations([
        WidgetRecommendationName.PDP_SIMILAR_ITEMS,
        WidgetRecommendationName.PDP_YOU_MAY_ALSO_LIKE,
    ]);

    const recentlyViewedWidget = useWidgetRecommendations([
        WidgetRecommendationName.PDP_RECENTLY_VIEWED,
    ]);

    const enableStickyATBOnPDP = useCustomPreferenceOrDefault(
        CustomPreference.ENABLE_STICKY_ATB_ON_PDP,
        true,
    );

    const { isWriteReviewModalOpen, onToggleWriteReviewModal, reviewsRef } =
        useTurnToComponentData();

    const location = useLocation();

    const { height: headerHeight = 0 } = useResizeObserver({
        ref: getElementRef('layout-header'),
    });
    const { height: buyBoxHeight = 0 } = useResizeObserver({ ref: getElementRef('buy-box') });
    const { topPosition } = useGetTopBuyBoxPosition({ headerHeight, buyBoxHeight });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!sku || !pageURL) {
        if (isSSRPreviewMode && isServer) {
            console.warn('Preview mode: suppressing error', error);
            return <LoadingIndicator isLoading />;
        }
        throw new BFFHttpError(404, 'Not Found', {});
    }

    if (isError && error) {
        if (isSSRPreviewMode && isServer) {
            console.warn('Preview mode: suppressing error', error);
            return <LoadingIndicator isLoading />;
        }
        throw buildHttpError(error);
    }

    if (!product) {
        return <PDPSkeleton />;
    }

    // Do the redirection if the product doesn't match its URL
    // This also works for PRO items and any non-hardwood product landing here.
    if (
        !isLoading &&
        product.id === sku &&
        product.c_pwaUrl &&
        `${PageUrl.PRODUCT}/${pageURL}/${sku}` !== product.c_pwaUrl
    ) {
        return <Redirect to={product.c_pwaUrl} />;
    }

    const canonicalUrl = getCanonicalUrl({
        id: product.c_canonicalProductId ?? '',
        sku: product.c_pageURL ?? '',
        category: CanonicalCategory.PRODUCT,
    });

    const ogImage = product.c_imageUrl
        ? getSourcePropsByWidth(product.c_imageUrl).defaultSrc
        : undefined;

    const shouldNoIndex = isNoIndexPage(location?.pathname);

    return (
        <ErrorBoundary FallbackComponent={EnhancedError}>
            <PDPHead />
            <Seo
                title={parse(product?.pageTitle || '') as string}
                description={product?.pageDescription}
                keywords={product?.pageKeywords}
                noIndex={shouldNoIndex}
            />
            <Helmet>
                {/* canonical link with reference to master product id */}
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <ProductSchema product={product} />
            <VideoSchema product={product} />
            <QuestionSchema product={product} />
            <ImageSchema product={product} />
            <OrganizationSchema product={product} />
            <OpenGraph
                url={canonicalUrl}
                title={product.name ?? ''}
                description={product.shortDescription ?? product.longDescription ?? ''}
                imageUrl={ogImage}
                type="product"
            />
            <Flex
                data-testid="pdp-container"
                direction="column"
                background="white"
                alignItems="center"
                paddingX={{ base: 0, md: 8, lg: 10, xl: 16 }}
            >
                <Box width="100%" paddingY={4} paddingX={{ base: 4, sm: 8, md: 0 }}>
                    <Breadcrumb itemsList={breadcrumbs} />
                    <BreadcrumbSchema breadcrumbTrail={breadcrumbs} />
                </Box>

                <Flex
                    justifyContent="center"
                    width="100%"
                    maxWidth={PDP_MAX_WIDTH}
                    direction={{ base: 'column', md: 'row' }}
                    className="xs:max-md:space-y-8 md:space-x-8"
                >
                    <Flex direction="column" className="gap-16" width={{ base: '100%', md: '60%' }}>
                        <Flex
                            data-testid="pdp-images"
                            alignItems={isLoading ? 'center' : 'flex-start'}
                            justifyContent="center"
                        >
                            <PhotoGrid productId={sku} badge={product?.c_badge} />
                        </Flex>
                        <Box w="fit-content" h="fit-content" hideBelow="md">
                            <ProductInformation product={product} />
                        </Box>
                    </Flex>

                    <Flex
                        width={{ base: '100%', md: '30%' }}
                        direction="column"
                        alignItems="stretch"
                        flexGrow={1}
                        position="relative"
                    >
                        <Flex
                            data-testid="pdp-buy-box"
                            ref={registerElementRef('buy-box')}
                            height="fit-content"
                            top={{
                                md: `${topPosition}px`,
                            }}
                            position={{
                                base: 'relative',
                                md: 'sticky',
                            }}
                        >
                            <BuyBox headerHeight={headerHeight} />
                        </Flex>
                    </Flex>
                    <Box w="fit-content" h="fit-content" hideFrom="md">
                        <ProductInformation product={product} />
                    </Box>
                </Flex>

                {/* First two recommendation widgets names: pdp_recently_viewed_products and pdp_you_may_also_like */}
                {!isCustomerGetLoading && recommendationWidgets.length > 0 && (
                    <Box
                        ref={registerElementRef('recommendations-container')}
                        className="w-full space-y-16 xs:px-4 sm:px-8 md:px-0"
                        marginTop={16}
                        alignItems={{ xl: 'center' }}
                        maxWidth={PDP_MAX_WIDTH}
                    >
                        {recommendationWidgets.map((widget) => (
                            <ClientRecommendations
                                key={widget.id}
                                widget={widget}
                                location={RecommendationLocation.PDP}
                                productId={product?.master?.masterId}
                                categoryId={product?.c_primaryCategory?.id}
                                brandCategoryId={product?.c_brandCategoryId}
                                collectionId={product?.c_collectionId}
                                productTypeFilter="hardgood"
                                userId={userId}
                            />
                        ))}
                    </Box>
                )}
            </Flex>

            <Box marginTop={16}>
                <ClientOnly hideBelowTheFold skeleton={<TurnToSkeleton />}>
                    <div data-testid="turn-to-defer" ref={reviewsRef} className="w-full">
                        <TurnToProvider productId={product.master?.masterId || product.id}>
                            <TurnTo productId={product.master?.masterId || product.id} />
                        </TurnToProvider>
                    </div>
                </ClientOnly>
                <WriteReviewFlow
                    onCloseWriteReview={() => onToggleWriteReviewModal(false)}
                    onOpenWriteReview={() => onToggleWriteReviewModal(true)}
                    isWriteReviewOpen={isWriteReviewModalOpen}
                />
            </Box>

            {/* widget for recently viewed products */}
            {!isCustomerGetLoading && recentlyViewedWidget && (
                <Flex
                    data-testid="pdp-container"
                    direction="column"
                    background="white"
                    alignItems="center"
                    paddingX={{ base: 0, md: 8, lg: 10, xl: 16 }}
                >
                    <Box
                        className="w-full space-y-16 xs:px-4 sm:px-8 md:px-0"
                        marginTop={16}
                        alignItems={{ xl: 'center' }}
                        maxWidth={PDP_MAX_WIDTH}
                    >
                        {recentlyViewedWidget.map((widget) => (
                            <ClientRecommendations
                                key={widget.id}
                                widget={widget}
                                location={RecommendationLocation.PDP}
                                productId={product?.master?.masterId}
                                categoryId={product?.c_primaryCategory?.id}
                                brandCategoryId={product?.c_brandCategoryId}
                                collectionId={product?.c_collectionId}
                                userId={userId}
                                productTypeFilter="hardgood"
                            />
                        ))}
                    </Box>
                </Flex>
            )}

            <ClientOnly>
                <ProductRelatedCategories className="mt-12 px-4 sm:px-8 lg:px-10 xl:px-16" />
            </ClientOnly>

            <ProductsAddedToBagDrawer />
            {enableStickyATBOnPDP ? <StickyAddToBagSection /> : null}
        </ErrorBoundary>
    );
};

const PDP: PageFC<
    {
        product?: Product;
        assets?: AssetInfo[];
        isRedirected?: boolean;
        headerRef?: React.MutableRefObject<HTMLDivElement | null>;
    },
    {
        location?: Location<PDPLocationState>;
    }
> = ({ product, assets, headerRef, location }) => {
    const { pageURL, sku } = useParams<{
        pageURL: string;
        sku: string;
    }>();

    const initialValues = useMemo(() => {
        if (location?.state?.shippingOptionType) {
            return {
                selectedShippingOptionType: location.state.shippingOptionType,
            };
        }
        return undefined;
    }, [location?.state]);

    return (
        <SSRProductProvider product={product} assets={assets}>
            <PdpProvider productId={sku} initialValues={initialValues}>
                <TurnToComponentDataProvider>
                    <PDPLayout sku={sku} pageURL={pageURL} headerRef={headerRef} />
                </TurnToComponentDataProvider>
            </PdpProvider>
        </SSRProductProvider>
    );
};

PDP.defaultProps = {
    product: undefined,
    assets: undefined,
    headerRef: undefined,
};

export default PDP;
